import { matchPath, useLocation } from 'react-router-dom';

import { IS_SIGNED_IN } from '~/constants';
import { PUBLIC_ROUTES } from '~c/components/public-routes';
import { useSelector } from '~c/hooks/redux';
import { useReadLocalStorage, useUpdateEffect } from '~c/hooks/use-hooks';
import { userProfileApi, nodeAuthApi, type AnchUserInfo } from '~c/services';

export type { AnchUserInfo };

export interface UserProfileT {
  access?: string;
  joinDate?: number;
  firstName?: string;
  lastName?: string;
  hasSelectedProfile: boolean;
  jobTitle?: string;
  organization?: string;
  role?: string[];
  emailVerified: boolean;
  lastLogin?: Date;
  orgId?: string;
  email?: string;
  otpEnabled: boolean;
  // Multi-tenant fields
  defaultOrganizationId?: string;
  organizations?: string[];
  isSystemAdmin?: boolean;
  isBetaUser?: boolean;
  currentOrganizationRole?: 'ORG_ADMIN' | 'MEMBER';
  currentOrganizationName?: string;
  // Anch integration
  anchUserInfo?: AnchUserInfo;
}
interface UserProfileResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  userProfile: UserProfileT;
  refetch: () => void;
}

const DEFAULT_RESULT: UserProfileResult = {
  isAuthenticated: false,
  isLoading: true,
  userProfile: {
    emailVerified: false,
    hasSelectedProfile: false,
    otpEnabled: false,
    isSystemAdmin: false,
    isBetaUser: false,
  },
  refetch: () => {},
};

const SKIPPED_RESULT: UserProfileResult = {
  ...DEFAULT_RESULT,
  isLoading: false,
};

const PUBLIC_ROUTE_PATTERNS = Object.values(PUBLIC_ROUTES);

function useIsPublicRoute(): boolean {
  const location = useLocation();
  return PUBLIC_ROUTE_PATTERNS.some(
    (pattern) => matchPath(pattern, location.pathname) != null
  );
}

interface UseUserProfileOptions {
  skip?: boolean;
}

export function useUserProfile(options?: UseUserProfileOptions) {
  const isPublicRoute = useIsPublicRoute();
  const skip = options?.skip ?? isPublicRoute;

  // ===== ALL HOOKS MUST BE CALLED UNCONDITIONALLY (React Rules of Hooks) =====
  // Hooks must be called in the same order on every render.
  // Use 'skip' options to prevent actual API calls, but always call the hooks.

  const [signOut] = nodeAuthApi.useLogoutMutation();
  const userResult = nodeAuthApi.useCurrentUserQuery(undefined, { skip });
  const profileResult = userProfileApi.useGetProfileQuery(
    userResult.data?.auth_uid ?? '',
    { skip: skip || userResult.data?.auth_uid == null }
  );

  // These hooks must always be called, regardless of skip
  userProfileApi.endpoints.getProfile.useLazyQuery();
  const isSignedIn = useReadLocalStorage(IS_SIGNED_IN);
  /**
   * userProfile in the redux state is a separate store for storing user profile details
   * This should be migrated away in favor of the cache stored in useGetProfileQuery
   */
  const userProfile = useSelector((state) => state.userProfile);

  // Keep IS_SIGNED_IN in sync when currentUser is loaded (e.g. after login or refresh with valid cookies)
  useUpdateEffect(() => {
    if (userResult.data != null && typeof window !== 'undefined') {
      window.localStorage.setItem(IS_SIGNED_IN, 'true');
    }
  }, [userResult.data]);

  useUpdateEffect(() => {
    // Read current value from localStorage so we don't rely on stale hook state
    // (e.g. right after login the hook may not have re-rendered with the new value)
    const currentFlag =
      typeof window !== 'undefined'
        ? window.localStorage.getItem(IS_SIGNED_IN)
        : null;
    const shouldSignOut = !currentFlag && !skip;
    // This logic gets triggered for other browser tabs when the localstorage
    // is cleared. And it would allow other browser window to execute the logout actions
    // Skip signOut if we're on a public page
    if (shouldSignOut) {
      signOut();
    }
  }, [isSignedIn, skip]);

  // Return early if skipping (on public pages) - AFTER all hooks have been called
  if (skip) {
    return SKIPPED_RESULT;
  }

  // Skip all auth queries on public routes (e.g. /sign-in, /sign-up)
  // to avoid wasteful 401 responses
  if (isPublicRoute) {
    return SKIPPED_RESULT;
  }

  let result = DEFAULT_RESULT;
  if (
    // authentication failed
    userResult.isError ||
    // current user is null (probably as a result of dispatching logout)
    (userResult.isSuccess && userResult.data == null)
  ) {
    result.isLoading = false;
    result.refetch = () => {
      userResult.refetch();
      profileResult.refetch();
    };
    return result;
  }

  const profile = profileResult.data;
  // This is a quick hack to make sure the old redux state userProfile is properly initialized as well.
  // The userProfile redux initial state only has onlyOneOrg and hasSelectedProfile properties
  // Once useGetProfileQuery is fulfilled, these fields will be included in the redux state
  // The correct fix is to remove an userProfile redux state, and soley rely on the state within
  // useGetProfileQuery
  const userProfileInitialized = 'firstName' in userProfile;
  if (profile && userProfileInitialized) {
    /**
     * If access property missing, then user profile is returned is incomplete,
     * but it is still considered to be authenticated. (This happens when user
     * first registers the app, but has not provided org information)
     *
     * If access is available, then user is only authenticated when it is true.
     */
    const isAuthenticated = !('access' in profile) || !!profile.access;
    const {
      access,
      joinDate,
      firstName,
      lastName,
      hasSelectedProfile,
      jobTitle,
      organization,
      role,
      emailVerified,
      lastLogin,
      orgId,
      email,
      otpEnabled,
      defaultOrganizationId,
      organizations,
      isSystemAdmin,
      isBetaUser,
      currentOrganizationRole,
      anchUserInfo,
    } = profile;
    result = {
      isLoading: false,
      isAuthenticated,
      userProfile: {
        access: access ? access : undefined,
        joinDate: joinDate ? joinDate : undefined,
        firstName: firstName ? firstName : undefined,
        lastName: lastName ? lastName : undefined,
        hasSelectedProfile: !!hasSelectedProfile,
        jobTitle: jobTitle ? jobTitle : undefined,
        organization: organization ? organization : undefined,
        role: role ? role : undefined,
        emailVerified: !!emailVerified,
        lastLogin: lastLogin ? lastLogin : undefined,
        orgId: orgId ? orgId : undefined,
        email: email ? email : undefined,
        otpEnabled: otpEnabled ? otpEnabled : false,
        defaultOrganizationId: defaultOrganizationId
          ? defaultOrganizationId
          : undefined,
        organizations: organizations ? organizations : undefined,
        isSystemAdmin: isSystemAdmin ? isSystemAdmin : false,
        isBetaUser: isBetaUser ? isBetaUser : false,
        currentOrganizationRole: currentOrganizationRole
          ? currentOrganizationRole
          : undefined,
        currentOrganizationName: organization ? organization : undefined,
        anchUserInfo: anchUserInfo ? anchUserInfo : undefined,
      },
      refetch: () => {
        userResult.refetch();
        profileResult.refetch();
      },
    };
  }

  return result;
}
